import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ip_back } from "../../config";
import AgentMark from "../../digiping/AgentMark";
import DigipingHostMark from "../../digiping/DigipingHostMark";
import AddIcon from "@mui/icons-material/Add";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { useBrand } from "../../context/BrandContext";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import { readStoredUser } from "../../utils/homeRole";
import { useTourDemoActive } from "../../tour/useTourDemoActive";
import {
  demoParentChildren,
  demoParentProgress,
  isTourDemoId,
} from "../../tour/tourDemoFixtures";
import "./ParentProgressHub.css";

const getApiUrl = (endpoint) => {
  const base = (ip_back || "").toString().replace(/\/+$/, "");
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
};

function resolveChildImageUrl(url) {
  if (!url) return null;
  const raw = String(url).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) {
    return raw;
  }
  const base = (ip_back || "").toString().replace(/\/+$/, "");
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function parseHubSessionDate(value) {
  const raw = String(value || "").slice(0, 10);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (!value) return null;
  const when = new Date(value);
  return Number.isNaN(when.getTime()) ? null : when;
}

function readClubHints() {
  try {
    const user = readStoredUser() || {};
    const details = user.club_details || user.club || {};
    return {
      club_name: details.club_name || user.club_name || "",
      club_image:
        details.club_image || details.logo_url || user.club_image || null,
    };
  } catch {
    return { club_name: "", club_image: null };
  }
}

export default function ParentProgressHub() {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const locale = (i18n.language || "en").split("-")[0];

  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [rsvpBusyKey, setRsvpBusyKey] = useState(null);
  const [medicalNotesDraft, setMedicalNotesDraft] = useState("");
  const [medicalNotesDirty, setMedicalNotesDirty] = useState(false);
  const [medicalNotesBusy, setMedicalNotesBusy] = useState(false);
  const [medicalNotesSavedFlash, setMedicalNotesSavedFlash] = useState(false);
  const [loginEmailDraft, setLoginEmailDraft] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginFeedback, setLoginFeedback] = useState(null);
  const photoInputRef = useRef(null);
  const tourDemo = useTourDemoActive();

  const kids = tourDemo ? demoParentChildren() : children;
  const effectiveChildId = kids.some((c) => c.child_player_id === activeChildId)
    ? activeChildId
    : kids[0]?.child_player_id || null;
  const demoHub = demoParentProgress(effectiveChildId || "tour-demo-lea");
  const hub = tourDemo ? demoHub : progressData;
  const nextSessions = tourDemo ? demoHub.nextSessions : hub?.nextSessions || [];
  const activeChildMeta =
    kids.find((c) => c.child_player_id === effectiveChildId) || null;
  const multiClubFamily = useMemo(() => {
    const ids = new Set(
      kids.map((c) => String(c.club_id || "")).filter(Boolean)
    );
    return ids.size > 1;
  }, [kids]);

  // Header follows the selected child's club — siblings may train at different clubs.
  const clubMeta = useMemo(() => {
    const hints = readClubHints();
    const childClubName =
      hub?.child?.club_name || activeChildMeta?.club_name || "";
    const childClubImage =
      hub?.child?.club_image || activeChildMeta?.club_image || null;
    return {
      name:
        childClubName ||
        brand?.brand_name ||
        brand?.club_name ||
        hints.club_name ||
        t("club") ||
        "Club",
      logo:
        resolveClubImageUrl(childClubImage) ||
        resolveClubImageUrl(brand?.logo_url) ||
        resolveClubImageUrl(brand?.logo_light_url) ||
        resolveClubImageUrl(hints.club_image),
    };
  }, [brand, t, hub, activeChildMeta]);

  useEffect(() => {
    const fetchChildren = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(getApiUrl("/api/parent/children"), { headers });

        if (res.data?.children && res.data.children.length > 0) {
          setChildren(res.data.children);
          setActiveChildId(res.data.children[0].child_player_id);
        } else {
          setChildren([]);
          setActiveChildId(null);
          setProgressData(null);
        }
      } catch (err) {
        console.warn("Could not load parent children from API:", err.message);
        setChildren([]);
        setActiveChildId(null);
        setProgressData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchChildren();
  }, []);

  useEffect(() => {
    if (!activeChildId || isTourDemoId(activeChildId)) return;

    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(
          getApiUrl(`/api/parent/child-progress/${activeChildId}`),
          { headers }
        );
        if (res.data?.data) {
          setProgressData(res.data.data);
          return;
        }
        setProgressData(null);
      } catch (e) {
        console.warn("Could not load child progress:", e.message);
        setProgressData(null);
      }
    };

    fetchProgress();
  }, [activeChildId]);

  useEffect(() => {
    const notes = hub?.child?.medical_notes || "";
    setMedicalNotesDraft(notes);
    setMedicalNotesDirty(false);
    setMedicalNotesSavedFlash(false);
  }, [activeChildId, hub?.child?.medical_notes]);

  useEffect(() => {
    setLoginEmailDraft("");
    setLoginFeedback(null);
    setLoginBusy(false);
  }, [activeChildId]);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const applyChildImage = (playerId, imageUrl) => {
    setChildren((prev) =>
      prev.map((c) =>
        c.child_player_id === playerId ? { ...c, user_image: imageUrl } : c
      )
    );
    setProgressData((prev) => {
      if (!prev?.child || prev.child.child_player_id !== playerId) return prev;
      return {
        ...prev,
        child: { ...prev.child, user_image: imageUrl },
      };
    });
  };

  const uploadChildPhoto = async (file) => {
    const playerId = effectiveChildId;
    if (!file || !playerId || isTourDemoId(playerId)) return;
    setPhotoBusy(true);
    try {
      const form = new FormData();
      form.append("user_image", file);
      form.append("childData", JSON.stringify({}));
      const res = await axios.put(
        getApiUrl(`/api/parent/child/${playerId}`),
        form,
        { headers: authHeaders() }
      );
      const imageUrl = res.data?.child?.user_image || null;
      if (imageUrl) applyChildImage(playerId, imageUrl);
    } catch (err) {
      console.warn("Child photo upload failed:", err.message);
      window.alert(
        err.response?.data?.message ||
          t("parentHubPhotoUploadFailed") ||
          "Could not upload photo. Please try again."
      );
    } finally {
      setPhotoBusy(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const setSessionRsvp = async (tsId, status) => {
    if (!activeChildId || !tsId || !status) return;
    if (isTourDemoId(activeChildId) || isTourDemoId(tsId)) return;
    const busyKey = `${tsId}:${status}`;
    setRsvpBusyKey(busyKey);
    try {
      const res = await axios.post(
        getApiUrl(`/api/parent/child/${activeChildId}/session/${tsId}/rsvp`),
        { status },
        { headers: authHeaders() }
      );
      const nextStatus = res.data?.rsvp?.status || "pending";
      const nextPresent =
        res.data?.rsvp?.ts_present === undefined
          ? null
          : res.data.rsvp.ts_present;
      setProgressData((prev) => {
        if (!prev?.nextSessions) return prev;
        return {
          ...prev,
          nextSessions: prev.nextSessions.map((s) =>
            s.id === tsId || s.ts_id === tsId
              ? { ...s, status: nextStatus, ts_present: nextPresent }
              : s
          ),
        };
      });
    } catch (err) {
      console.warn("Session RSVP failed:", err.message);
      window.alert(
        err.response?.data?.message ||
          t("parentHubRsvpFailed") ||
          "Could not update attendance. Please try again."
      );
    } finally {
      setRsvpBusyKey(null);
    }
  };

  const saveMedicalNotes = async () => {
    if (!activeChildId || isTourDemoId(activeChildId)) return;
    setMedicalNotesBusy(true);
    setMedicalNotesSavedFlash(false);
    try {
      const notes = medicalNotesDraft.trim();
      const res = await axios.put(
        getApiUrl(`/api/parent/child/${activeChildId}`),
        { childData: { medicalNotes: notes } },
        { headers: authHeaders() }
      );
      const saved =
        res.data?.child?.medical_notes != null
          ? res.data.child.medical_notes
          : notes || null;
      setProgressData((prev) => {
        if (!prev?.child) return prev;
        return {
          ...prev,
          child: { ...prev.child, medical_notes: saved },
        };
      });
      setChildren((prev) =>
        prev.map((c) =>
          c.child_player_id === activeChildId
            ? { ...c, medical_notes: saved }
            : c
        )
      );
      setMedicalNotesDraft(saved || "");
      setMedicalNotesDirty(false);
      setMedicalNotesSavedFlash(true);
    } catch (err) {
      console.warn("Medical notes save failed:", err.message);
      window.alert(
        err.response?.data?.message ||
          t("parentHubMedicalNotesSaveFailed") ||
          "Could not save medical notes. Please try again."
      );
    } finally {
      setMedicalNotesBusy(false);
    }
  };

  const openChildLogin = async () => {
    const child = kids.find((c) => c.child_player_id === effectiveChildId);
    if (!child?.child_player_id || isTourDemoId(child.child_player_id)) return;
    const email = loginEmailDraft.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setLoginFeedback({
        type: "error",
        text:
          t("parentOpenAccountEmailRequired") ||
          "Enter the child's personal email.",
      });
      return;
    }
    setLoginBusy(true);
    setLoginFeedback(null);
    try {
      const res = await axios.post(
        getApiUrl("/api/parent/enable-child-login"),
        { playerId: child.child_player_id, email },
        { headers: authHeaders() }
      );
      if (res.data?.success) {
        setChildren((prev) =>
          prev.map((c) =>
            c.child_player_id === child.child_player_id
              ? {
                  ...c,
                  email,
                  has_own_login: false,
                  awaiting_password_setup: true,
                  can_open_player_account: true,
                }
              : c
          )
        );
        setLoginEmailDraft("");
        setLoginFeedback({
          type: "ok",
          text:
            t("parentOpenAccountSuccess", { name: child.first_name }) ||
            `Player account opened for ${child.first_name}. A confirmation email was sent so they can set their own password.`,
        });
      } else {
        setLoginFeedback({
          type: "error",
          text:
            res.data?.message ||
            t("parentOpenAccountFailed") ||
            "Could not open the player account.",
        });
      }
    } catch (err) {
      setLoginFeedback({
        type: "error",
        text:
          err.response?.data?.message ||
          err.message ||
          t("parentOpenAccountFailed") ||
          "Could not open the player account.",
      });
    } finally {
      setLoginBusy(false);
    }
  };

  const coachNotes = hub?.coachNotes || [];
  const activeChildImage =
    resolveChildImageUrl(hub?.child?.user_image) ||
    resolveChildImageUrl(activeChildMeta?.user_image);

  if (loading && !tourDemo) {
    return (
      <div className="parent-hub-container">
        <div className="parent-hub-shell">
          <header className="parent-hub-header parent-hub-header--brand">
            <div className="parent-hub-brand" data-tour-id="parent-hub">
              {clubMeta.logo ? (
                <img
                  src={clubMeta.logo}
                  alt={clubMeta.name || ""}
                  className="parent-hub-club-logo"
                />
              ) : (
                <span className="parent-hub-club-logo parent-hub-club-logo--fallback" aria-hidden>
                  {(clubMeta.name || "C").slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="parent-hub-title">
                <h1>{t("parentHubTitle") || "Parent Progress Hub"}</h1>
                <p className="parent-hub-club-name">{clubMeta.name}</p>
              </div>
            </div>
          </header>
          <p className="parent-hub-empty">{t("loading") || "Loading…"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-hub-container">
      <div className="parent-hub-shell">
        <header className="parent-hub-header parent-hub-header--brand">
          <div className="parent-hub-brand" data-tour-id="parent-hub">
            {clubMeta.logo ? (
              <img
                src={clubMeta.logo}
                alt={clubMeta.name || ""}
                className="parent-hub-club-logo"
              />
            ) : (
              <span className="parent-hub-club-logo parent-hub-club-logo--fallback" aria-hidden>
                {(clubMeta.name || "C").slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="parent-hub-title">
              <h1>{t("parentHubTitle") || "Parent Progress Hub"}</h1>
              <p>
                {clubMeta.name
                  ? `${clubMeta.name} · ${
                      t("parentHubSubtitle") ||
                      "Track milestones, attendance, and joy of play"
                    }`
                  : t("parentHubSubtitle") ||
                    "Track milestones, attendance, and joy of play"}
              </p>
            </div>
          </div>

          <div className="sibling-switcher-bar" data-tour-id="parent-children">
            {kids.map((child) => (
              <div
                key={child.child_player_id}
                className={`sibling-pill ${
                  child.child_player_id === effectiveChildId ? "active" : ""
                }`}
                onClick={() => setActiveChildId(child.child_player_id)}
              >
                <div className="sibling-pill-avatar">
                  {resolveChildImageUrl(child.user_image) ? (
                    <img
                      src={resolveChildImageUrl(child.user_image)}
                      alt=""
                      className="sibling-pill-avatar-img"
                    />
                  ) : child.first_name ? (
                    child.first_name[0].toUpperCase()
                  ) : (
                    "C"
                  )}
                </div>
                <div className="sibling-pill-info">
                  <span className="sibling-pill-name">{child.first_name}</span>
                  <span className="sibling-pill-sub">
                    {child.age != null
                      ? t("parentHubAgeYears", { age: child.age }) ||
                        `${child.age} yrs`
                      : ""}
                    {child.companion?.displayName
                      ? ` • ${child.companion.displayName}`
                      : ""}
                    {multiClubFamily && child.club_name
                      ? ` • ${child.club_name}`
                      : ""}
                  </span>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="add-sibling-btn"
              onClick={() => navigate("/parent-profiling")}
              title={t("parentAddChild") || "Add Child"}
            >
              <AddIcon fontSize="small" />
              <span>{t("parentAddChild") || "Add Child"}</span>
            </button>
          </div>
        </header>

        {!kids.length && (
          <div className="parent-hub-empty-card">
            <p>
              {t("parentHubNoChildren") ||
                "No children linked yet. Add a child to start tracking progress."}
            </p>
            <button
              type="button"
              className="add-sibling-btn"
              onClick={() => navigate("/parent-profiling")}
            >
              <AddIcon fontSize="small" />
              <span>{t("parentAddChild") || "Add Child"}</span>
            </button>
          </div>
        )}

        {hub && (
          <div className="parent-hero-card">
            <div className="parent-hero-path" data-tour-id="parent-smart">
            <div className="parent-hero-top">
              <div className="parent-hero-child">
                <div
                  className="parent-hero-avatar-wrap parent-hero-avatar-wrap--photo"
                  onClick={() => !photoBusy && photoInputRef.current?.click()}
                >
                  {activeChildImage ? (
                    <img
                      src={activeChildImage}
                      alt=""
                      className="parent-hero-avatar-img"
                    />
                  ) : (hub.child?.companion?.agentId || "pingwi") ===
                    "pingflo" ? (
                    <DigipingHostMark
                      clubLogoSrc={clubMeta.logo}
                      size={44}
                      agentId="pingflo"
                      title={clubMeta.name || "Pingflo"}
                    />
                  ) : (
                    <AgentMark
                      agentId={hub.child?.companion?.agentId || "pingwi"}
                      size={44}
                    />
                  )}
                  <button
                    type="button"
                    className="parent-hero-photo-btn"
                    disabled={photoBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      photoInputRef.current?.click();
                    }}
                    title={
                      t("parentHubUploadPhoto") || "Upload profile picture"
                    }
                    aria-label={
                      t("parentHubUploadPhoto") || "Upload profile picture"
                    }
                  >
                    <PhotoCameraOutlinedIcon fontSize="inherit" />
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*,.heic,.heif,.jpeg,.jpg,.png,.webp"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadChildPhoto(file);
                    }}
                  />
                </div>
                <div className="parent-hero-details">
                  <h2>
                    {t("parentHubChildJourney", {
                      name: hub.child?.first_name,
                    }) || `${hub.child?.first_name}'s Journey`}
                  </h2>
                  <p>
                    {t("parentHubCompanionLevel", {
                      companion: hub.child?.companion?.displayName,
                      level: hub.metrics.currentLevel,
                      title: hub.metrics.levelTitle,
                    }) ||
                      `${hub.child?.companion?.displayName} • Level ${hub.metrics.currentLevel}: ${hub.metrics.levelTitle}`}
                  </p>
                  <button
                    type="button"
                    className="parent-hero-photo-link"
                    disabled={photoBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      photoInputRef.current?.click();
                    }}
                  >
                    {photoBusy
                      ? t("parentHubUploadingPhoto") || "Uploading…"
                      : activeChildImage
                        ? t("parentHubChangePhoto") || "Change photo"
                        : t("parentHubUploadPhoto") || "Upload profile picture"}
                  </button>
                </div>
              </div>

              <div className="parent-hero-metrics">
                <div className="hero-metric-item">
                  <span className="val">{hub.metrics.sessionsAttended}</span>
                  <span className="lbl">
                    {t("parentHubSessionsAttended") || "Sessions Attended"}
                  </span>
                </div>
                <div className="hero-metric-item">
                  <span className="val">
                    {hub.metrics.weeksActive ??
                      hub.metrics.streakWeeks ??
                      0}{" "}
                    {t("parentHubWeeksShort") || "wks"}
                  </span>
                  <span className="lbl">
                    {t("parentHubWeeksActive") ||
                      t("parentHubActiveStreak") ||
                      "Weeks active"}
                  </span>
                </div>
              </div>
            </div>

            <div className="level-progress-wrap">
              <div className="level-progress-header">
                <span>
                  {t("parentHubProgressToLevel", {
                    level: hub.metrics.currentLevel + 1,
                  }) ||
                    `Progress to Level ${hub.metrics.currentLevel + 1}`}
                </span>
                <span>
                  {t("parentHubSessionsUntilBadge", {
                    count: hub.metrics.nextMilestoneSessionsRemaining,
                  }) ||
                    `${hub.metrics.nextMilestoneSessionsRemaining} session until next badge!`}
                </span>
              </div>
              <div className="level-progress-bar-bg">
                <div
                  className="level-progress-bar-fill"
                  style={{
                    width: `${Math.min(
                      100,
                      (hub.metrics.sessionsAttended % 4) * 25
                    )}%`,
                  }}
                />
              </div>
            </div>
            </div>

            <div className="parent-medical-notes" data-tour-id="parent-medical">
              <div className="parent-medical-notes-header">
                <h3>
                  {t("parentHubMedicalNotesTitle") || "Medical notes"}
                </h3>
                {medicalNotesSavedFlash ? (
                  <span className="parent-medical-notes-saved">
                    {t("parentHubMedicalNotesSaved") || "Saved"}
                  </span>
                ) : null}
              </div>
              <p className="parent-medical-notes-help">
                {t("parentHubMedicalNotesHelp") ||
                  "Visible to you and shared with club coaches for safety. Leave blank if none."}
              </p>
              <textarea
                className="parent-medical-notes-input"
                rows={3}
                value={medicalNotesDraft}
                placeholder={
                  t("parentWizardMedicalNotesPlaceholder") ||
                  "Optional: Medical notes (e.g. asthma, allergies)"
                }
                onChange={(e) => {
                  setMedicalNotesDraft(e.target.value);
                  setMedicalNotesDirty(true);
                  setMedicalNotesSavedFlash(false);
                }}
              />
              <div className="parent-medical-notes-actions">
                <button
                  type="button"
                  className="parent-medical-notes-save"
                  disabled={medicalNotesBusy || !medicalNotesDirty}
                  onClick={saveMedicalNotes}
                >
                  {medicalNotesBusy
                    ? t("parentWizardSaving") || "Saving..."
                    : t("parentHubMedicalNotesSave") || "Save medical notes"}
                </button>
              </div>
            </div>

            {activeChildMeta?.can_open_player_account ? (
              <div
                className="parent-open-account"
                data-tour-id="parent-open-account"
              >
                <h3>
                  {t("parentOpenAccountTitle") || "Open player account"}
                </h3>
                <p className="parent-open-account-help">
                  {activeChildMeta.awaiting_password_setup
                    ? t("parentOpenAccountAwaiting", {
                        name: activeChildMeta.first_name,
                      }) ||
                      `Waiting for ${activeChildMeta.first_name} to confirm their email and set a password.`
                    : t("parentOpenAccountHint", {
                        age: activeChildMeta.min_self_login_age || 13,
                        name: activeChildMeta.first_name,
                      }) ||
                      `${activeChildMeta.first_name} is old enough for their own player account. You can both follow progress with Pingwi.`}
                </p>
                <div className="parent-open-account-row">
                  <input
                    type="email"
                    className="parent-open-account-input"
                    autoComplete="email"
                    placeholder={
                      t("parentOpenAccountEmailPlaceholder") ||
                      "Child's personal email"
                    }
                    value={loginEmailDraft}
                    disabled={loginBusy}
                    onChange={(e) => {
                      setLoginEmailDraft(e.target.value);
                      setLoginFeedback(null);
                    }}
                  />
                  <button
                    type="button"
                    className="parent-open-account-cta"
                    disabled={loginBusy}
                    onClick={openChildLogin}
                  >
                    {loginBusy
                      ? t("sending") || "Sending…"
                      : activeChildMeta.awaiting_password_setup
                        ? t("parentOpenAccountResend") || "Resend confirmation"
                        : t("parentOpenAccountCta") || "Open player account"}
                  </button>
                </div>
                {loginFeedback ? (
                  <p
                    className={`parent-open-account-feedback parent-open-account-feedback--${loginFeedback.type}`}
                    role="status"
                  >
                    {loginFeedback.text}
                  </p>
                ) : null}
              </div>
            ) : activeChildMeta?.has_own_login ? (
              <div className="parent-open-account parent-open-account--done">
                <p>
                  {t("parentOpenAccountAlreadyActive", {
                    name: activeChildMeta.first_name,
                  }) ||
                    `${activeChildMeta.first_name} already has their own player login${
                      activeChildMeta.email ? ` (${activeChildMeta.email})` : ""
                    }.`}
                </p>
              </div>
            ) : activeChildMeta && activeChildMeta.age != null ? (
              <p className="parent-open-account-too-young">
                {t("parentOpenAccountTooYoung", {
                  age: activeChildMeta.min_self_login_age || 13,
                }) ||
                  `A personal player login becomes available from age ${
                    activeChildMeta.min_self_login_age || 13
                  }. Until then, follow progress in the Hub with Pingwi.`}
              </p>
            ) : null}
          </div>
        )}

        {hub?.badges && (
          <section className="parent-hub-section">
            <div className="parent-hub-section-title">
              <h3>{t("parentHubBadgesTitle") || "Badges & Milestones"}</h3>
            </div>
            <div className="badges-grid">
              {hub.badges.map((b) => (
                <div
                  key={b.id}
                  className={`badge-card ${b.earned ? "earned" : "unearned"}`}
                >
                  <span className="badge-icon">{b.icon}</span>
                  <span className="badge-title">{b.title}</span>
                  <span className="badge-desc">{b.desc}</span>
                </div>
              ))}
            </div>
          </section>
        )}

            {(hub || nextSessions.length > 0) && (
          <section className="parent-hub-section" data-tour-id="parent-rsvp">
            <div className="parent-hub-section-title">
              <h3>
                {t("parentHubNextSessionsTitle") || "Next Training Sessions"}
              </h3>
            </div>
            {nextSessions.length > 0 ? (
              <div className="sessions-list">
                {nextSessions.map((sess) => {
                  const when = parseHubSessionDate(sess.date);
                  const weekday = when
                    ? when.toLocaleDateString(locale, { weekday: "short" })
                    : "—";
                  const dayNum = when
                    ? when.toLocaleDateString(locale, { day: "numeric" })
                    : "";
                  const monthLabel = when
                    ? when.toLocaleDateString(locale, { month: "short" })
                    : "";
                  const timeLabel = sess.start_time || "";
                  const groupLabel = sess.group_name || sess.title;
                  const status = sess.status || "pending";
                  const sessBusy =
                    rsvpBusyKey &&
                    (rsvpBusyKey.startsWith(`${sess.id}:`) ||
                      rsvpBusyKey.startsWith(`${sess.ts_id}:`));
                  return (
                    <div
                      key={sess.id || sess.ts_id}
                      className={`session-item-card session-item-card--${status}`}
                    >
                      <div className="session-item-main">
                        <div className="session-date-badge">
                          <div className="weekday">{weekday}</div>
                          <div className="day">{dayNum}</div>
                          <div className="month">{monthLabel}</div>
                        </div>
                        <div className="session-info">
                          <h4>{groupLabel}</h4>
                          <p>
                            {[
                              timeLabel,
                              sess.hall,
                              sess.table,
                              sess.coachName
                                ? t("parentHubLedBy", {
                                    name: sess.coachName,
                                  }) || `Led by ${sess.coachName}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" • ") ||
                              t("parentHubDetailsSoon") ||
                              "Details coming soon"}
                          </p>
                          <p className={`session-rsvp-status session-rsvp-status--${status}`}>
                            {status === "confirmed"
                              ? t("parentHubConfirmedAttending") ||
                                "Confirmed attending"
                              : status === "absent"
                                ? t("parentHubMarkedAbsent") ||
                                  "Marked not attending"
                                : t("parentHubRsvpPending") ||
                                  "Attendance not set"}
                          </p>
                        </div>
                      </div>
                      <div className="session-rsvp-actions">
                        <button
                          type="button"
                          className={`session-rsvp-btn session-rsvp-btn--present ${
                            status === "confirmed" ? "is-active" : ""
                          }`}
                          disabled={!!sessBusy}
                          onClick={() =>
                            setSessionRsvp(sess.ts_id || sess.id, "present")
                          }
                        >
                          {t("parentHubRsvpPresent") || "Will attend"}
                        </button>
                        <button
                          type="button"
                          className={`session-rsvp-btn session-rsvp-btn--absent ${
                            status === "absent" ? "is-active" : ""
                          }`}
                          disabled={!!sessBusy}
                          onClick={() =>
                            setSessionRsvp(sess.ts_id || sess.id, "absent")
                          }
                        >
                          {t("parentHubRsvpAbsent") || "Won't attend"}
                        </button>
                        {status !== "pending" ? (
                          <button
                            type="button"
                            className="session-rsvp-btn session-rsvp-btn--clear"
                            disabled={!!sessBusy}
                            onClick={() =>
                              setSessionRsvp(sess.ts_id || sess.id, "clear")
                            }
                          >
                            {t("parentHubRsvpClear") || "Clear"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="parent-hub-empty">
                {(hub?.trainingGroups || []).length
                  ? t("parentHubNoSessionsForGroups", {
                      groups: hub.trainingGroups
                        .map((g) => g.name)
                        .filter(Boolean)
                        .join(", "),
                    }) ||
                    `No upcoming sessions on the ${hub.trainingGroups
                      .map((g) => g.name)
                      .filter(Boolean)
                      .join(", ")} calendar yet.`
                  : t("parentHubNoGroups") ||
                    t("parentHubNoSessions") ||
                    "This child is not registered in a training group yet."}
              </p>
            )}
          </section>
        )}

        {hub && (
          <section className="parent-hub-section">
            <div className="parent-hub-section-title">
              <h3>
                {t("parentHubCoachNotesTitle") || "Coach Notes & Encouragement"}
              </h3>
            </div>
            {coachNotes.length > 0 ? (
              <div className="sessions-list">
                {coachNotes.map((note) => (
                  <div key={note.id} className="coach-note-card">
                    <div className="coach-note-header">
                      <span>
                        {note.coachName}
                        {note.source === "pingwi"
                          ? ` • ${
                              t("parentHubNoteViaPingwi") || "via Pingwi"
                            }`
                          : note.highlight
                            ? ` • ${t("parentHubFocus") || "Focus"}: ${
                                note.highlight
                              }`
                            : ""}
                      </span>
                      <span>
                        {note.date
                          ? new Date(note.date).toLocaleDateString(locale, {
                              month: "short",
                              day: "numeric",
                            })
                          : ""}
                      </span>
                    </div>
                    <div className="coach-note-text">"{note.message}"</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="parent-hub-empty">
                {t("parentHubNoCoachNotes") ||
                  "No coach notes yet. Notes and Pingwi messages from the coach will appear here."}
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
